import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { invoiceAPI, clientAPI, pdfAPI, notificationActionsAPI, exportAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatCurrency, getStatusColor, getPhaseLabel } from '../lib/utils';
import {
  Plus,
  Search,
  Receipt,
  Download,
  MoreHorizontal,
  CheckCircle,
  Printer,
  Mail,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const PHASES = [
  { value: 'gap_analysis', label: 'Gap Analysis' },
  { value: 'application_implementation', label: 'Application & Implementation' },
  { value: 'desktop_nc_closure', label: 'Desktop NC Closure' },
  { value: 'pre_assessment', label: 'Pre-Assessment' },
  { value: 'final_nc_closure', label: 'Final NC Closure' },
];

export const InvoicesPage = () => {
  const { isClient, isAdmin } = useAuth();
  usePageTitle('Invoices');
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    phase: '',
    items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
    notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;

      const [invoicesRes, clientsRes] = await Promise.all([
        invoiceAPI.getAll(params),
        isClient() ? Promise.resolve({ data: [] }) : clientAPI.getAll(),
      ]);

      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, isClient]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        items: formData.items.map((item) => ({
          ...item,
          amount: item.quantity * item.unit_price,
        })),
      };
      await invoiceAPI.create(payload);
      toast.success('Invoice created successfully!');
      setIsCreateOpen(false);
      setFormData({
        client_id: '',
        phase: '',
        items: [{ description: '', quantity: 1, unit_price: 0, amount: 0 }],
        notes: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await invoiceAPI.markPaid(invoiceId);
      toast.success('Invoice marked as paid!');
      loadData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await pdfAPI.getInvoicePdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendEmail = async (invoiceId) => {
    try {
      await notificationActionsAPI.sendInvoiceEmail(invoiceId);
      toast.success('Invoice email sent!');
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, amount: 0 }],
    });
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const getClientName = (invoice) => {
    if (invoice?.client_name) return invoice.client_name;
    const client = clients.find((c) => c.id === invoice?.client_id);
    return client?.organization_name || 'Unknown';
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  };

  const filteredInvoices = invoices.filter(
    (invoice) =>
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getClientName(invoice).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="invoices-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-emerald-600 via-green-500 to-teal-400 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">
              {isClient() ? 'My Invoices' : 'Invoice Management'}
            </h1>
            <p className="text-emerald-100">
              {isClient() ? 'View and download your invoices' : 'Create and manage client invoices'}
            </p>
          </div>
        {isAdmin() && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={async () => {
              try {
                const res = await exportAPI.invoices();
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'invoices_export.xlsx'; a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Invoices exported');
              } catch (e) { toast.error('Export failed'); }
            }} data-testid="export-invoices-btn">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create GST Invoice</DialogTitle>
                <DialogDescription>Generate a new invoice for a client</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_id">Client *</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                    >
                      <SelectTrigger data-testid="invoice-client-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.organization_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phase">Phase (Optional)</Label>
                    <Select
                      value={formData.phase}
                      onValueChange={(value) => setFormData({ ...formData, phase: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select phase" />
                      </SelectTrigger>
                      <SelectContent>
                        {PHASES.map((phase) => (
                          <SelectItem key={phase.value} value={phase.value}>
                            {phase.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <Label>Line Items</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-20">Qty</TableHead>
                          <TableHead className="w-32">Unit Price</TableHead>
                          <TableHead className="w-32">Amount</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Input
                                value={item.description}
                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                placeholder="Service description"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                value={item.unit_price}
                                onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(item.quantity * item.unit_price)}
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(index)}
                                disabled={formData.items.length === 1}
                              >
                                ×
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    + Add Line Item
                  </Button>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>GST (18%):</span>
                      <span className="font-medium">{formatCurrency(calculateSubtotal() * 0.18)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total:</span>
                      <span>{formatCurrency(calculateSubtotal() * 1.18)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                    placeholder="Additional notes..."
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="submit-invoice-btn">Create Invoice</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-invoices-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <Receipt className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">
                {formatCurrency(
                  invoices
                    .filter((i) => i.status === 'pending')
                    .reduce((sum, i) => sum + i.total_amount, 0)
                )}
              </p>
              <p className="text-sm text-amber-500 font-medium">Pending</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100 rounded-xl">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-700">
                {formatCurrency(
                  invoices
                    .filter((i) => i.status === 'paid')
                    .reduce((sum, i) => sum + i.total_amount, 0)
                )}
              </p>
              <p className="text-sm text-emerald-500 font-medium">Collected</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <Receipt className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-700">{invoices.length}</p>
              <p className="text-sm text-blue-500 font-medium">Total Invoices</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Invoices Table */}
      {filteredInvoices.length === 0 ? (
        <Card className="p-8 text-center">
          <Receipt className="w-12 h-12 mx-auto mb-2 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">No invoices found</p>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                {!isClient() && <TableHead>Client</TableHead>}
                <TableHead>Phase</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                  {!isClient() && <TableCell>{getClientName(invoice)}</TableCell>}
                  <TableCell>
                    {invoice.phase ? getPhaseLabel(invoice.phase) : '-'}
                  </TableCell>
                  <TableCell>{formatDate(invoice.created_at)}</TableCell>
                  <TableCell className="font-medium">
                    {formatCurrency(invoice.total_amount)}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(invoice.status)}>
                      {invoice.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setIsDetailOpen(true);
                          }}
                        >
                          <Printer className="w-4 h-4 mr-2" />
                          View Invoice
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadPdf(invoice)}>
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </DropdownMenuItem>
                        {isAdmin() && (
                          <DropdownMenuItem onClick={() => handleSendEmail(invoice.id)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Send via Email
                          </DropdownMenuItem>
                        )}
                        {isAdmin() && invoice.status === 'pending' && (
                          <DropdownMenuItem onClick={() => handleMarkPaid(invoice.id)}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark as Paid
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Invoice Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice {selectedInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <InvoicePreview invoice={selectedInvoice} clientName={getClientName(selectedInvoice)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InvoicePreview = ({ invoice, clientName }) => {
  return (
    <div className="space-y-6 p-4 bg-white border rounded-lg" data-testid="invoice-preview">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_accred-portal/artifacts/ux88ej0x_steward.png"
            alt="Steward Healthcare"
            className="h-12 mb-2"
          />
          <p className="text-sm text-muted-foreground">Quality Consultancy & Digital Health</p>
        </div>
        <div className="text-right">
          <h2 className="text-2xl font-bold">TAX INVOICE</h2>
          <p className="text-sm text-muted-foreground">{invoice.invoice_number}</p>
          <p className="text-sm text-muted-foreground">{formatDate(invoice.created_at)}</p>
        </div>
      </div>

      {/* Client Info */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
        <div>
          <p className="text-sm text-muted-foreground">Bill To:</p>
          <p className="font-medium">{clientName}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Status:</p>
          <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
        </div>
      </div>

      {/* Items */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead className="text-right">Unit Price</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoice.items?.map((item, index) => (
            <TableRow key={index}>
              <TableCell>{item.description}</TableCell>
              <TableCell className="text-right">{item.quantity}</TableCell>
              <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
              <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-64 space-y-2">
          <div className="flex justify-between text-sm">
            <span>Subtotal:</span>
            <span>{formatCurrency(invoice.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>GST ({invoice.gst_percentage}%):</span>
            <span>{formatCurrency(invoice.gst_amount)}</span>
          </div>
          <div className="flex justify-between font-bold text-lg border-t pt-2">
            <span>Total:</span>
            <span>{formatCurrency(invoice.total_amount)}</span>
          </div>
        </div>
      </div>

      {invoice.notes && (
        <div className="p-3 bg-slate-50 rounded-lg">
          <p className="text-sm text-muted-foreground">Notes: {invoice.notes}</p>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => window.print()}>
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
        <Button>
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
      </div>
    </div>
  );
};

export default InvoicesPage;
